import { collection, getDocs, query, where } from "firebase/firestore";
import { signOut } from "firebase/auth";
import { motion } from "framer-motion";
import { AlertCircle, Brain, Key, Users, LogOut, TrendingUp } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { useAuth } from "../contexts/AuthContext";
import { auth, db } from "../lib/firebase";
import { isUserAdmin } from "../lib/firebase-admin";

export function AdminDashboard() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const { isLoading: isAuthLoading, user } = useAuth();
  const [stats, setStats] = useState({
    totalCoaches: 0,
    totalPartners: 0,
    totalRespondents: 0,
    activeAssessments: 0,
  });

  const handleLogout = useCallback(async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (err) {
      console.error("Error signing out:", err);
    }
  }, [navigate]);

  useEffect(() => {
    if (!isAuthLoading && !user) {
      navigate("/login");
    }
    if (!isAuthLoading && user) {
      checkAdminAccess();
    }
  }, [isAuthLoading, user, navigate]);

  const checkAdminAccess = async () => {
    try {
      if (!user) return;

      const isAdmin = await isUserAdmin(user.uid);
      if (!isAdmin) {
        navigate("/");
        return;
      }

      if (user?.uid) {
        loadDashboardStats();
      }
    } catch (err) {
      console.error("Admin access check failed:", err);
      navigate("/");
    }
  };

  const loadDashboardStats = async () => {
    try {
      setIsLoading(true);

      // Get coaches count
      const coachesSnapshot = await getDocs(collection(db, "coaches"));
      const coachesCount = coachesSnapshot.size;

      // Get partners count
      const partnersSnapshot = await getDocs(collection(db, "partners"));
      const partnersCount = partnersSnapshot.size;

      // Get respondents count
      const respondentsSnapshot = await getDocs(collection(db, "respondents"));
      const respondentsCount = respondentsSnapshot.size;

      // Get active assessments (completed in last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const activeQuery = query(
        collection(db, "respondents"),
        where("createdAt", ">=", thirtyDaysAgo)
      );
      const activeSnapshot = await getDocs(activeQuery);
      const activeCount = activeSnapshot.size;

      setStats({
        totalCoaches: coachesCount,
        totalPartners: partnersCount,
        totalRespondents: respondentsCount,
        activeAssessments: activeCount,
      });
    } catch (err) {
      console.error("Failed to load dashboard stats:", err);
      setError("Failed to load dashboard statistics");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading admin dashboard..." />;
  }

  return (
    <div className="min-h-screen minimal-bg py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Brain className="w-12 h-12 text-blue-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600">Manage your assessment system</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 text-gray-700 
              hover:bg-gray-200 transition-all"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-500/10 text-red-400 p-4 rounded-xl mb-6">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}

        {/* Stats Overview */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl p-6 bg-blue-50 border border-blue-100 shadow-sm hover:shadow-md transition-all duration-300"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-2xl font-bold text-gray-900 mb-1">
              {stats.totalCoaches}
                </div>
                <div className="text-sm text-gray-600">Active Coaches</div>
              </div>
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="rounded-2xl p-6 bg-purple-50 border border-purple-100 shadow-sm hover:shadow-md transition-all duration-300"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-2xl font-bold text-gray-900 mb-1">
              {stats.totalPartners}
                </div>
                <div className="text-sm text-gray-600">Active Partners</div>
              </div>
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="rounded-2xl p-6 bg-emerald-50 border border-emerald-100 shadow-sm hover:shadow-md transition-all duration-300"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-2xl font-bold text-gray-900 mb-1">
              {stats.totalRespondents}
                </div>
                <div className="text-sm text-gray-600">Total Respondents</div>
              </div>
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Key className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="rounded-2xl p-6 bg-amber-50 border border-amber-100 shadow-sm hover:shadow-md transition-all duration-300"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-2xl font-bold text-gray-900 mb-1">
              {stats.activeAssessments}
                </div>
                <div className="text-sm text-gray-600">Assessments (30d)</div>
              </div>
              <div className="p-2 bg-amber-100 rounded-lg">
                <Brain className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl p-6 mb-8 shadow-sm border border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Quick Actions</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <button
              onClick={() => navigate("/admin/partners")}
              className="p-6 rounded-xl bg-blue-50 border border-blue-100 hover:bg-blue-100 transition-all text-left shadow-sm hover:shadow-md"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-lg font-semibold text-gray-900 mb-1">Manage Partners</div>
                  <div className="text-sm text-gray-600">
                    Create and manage partner accounts
                  </div>
                </div>
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </button>

            <button
              onClick={() => navigate("/admin/coaches")}
              className="p-6 rounded-xl bg-purple-50 border border-purple-100 hover:bg-purple-100 transition-all text-left shadow-sm hover:shadow-md"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-lg font-semibold text-gray-900 mb-1">Manage Coaches</div>
                  <div className="text-sm text-gray-600">
                    Create and manage coach accounts
                  </div>
                </div>
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </button>

            <button
              onClick={() => navigate("/admin/codes")}
              className="p-6 rounded-xl bg-emerald-50 border border-emerald-100 hover:bg-emerald-100 transition-all text-left shadow-sm hover:shadow-md"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-lg font-semibold text-gray-900 mb-1">Assessment Codes</div>
                  <div className="text-sm text-gray-600">
                    Generate and track assessment codes
                  </div>
                </div>
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Key className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </button>

            <button
              onClick={() => navigate("/admin/analytics")}
              className="p-6 rounded-xl bg-amber-50 border border-amber-100 hover:bg-amber-100 transition-all text-left shadow-sm hover:shadow-md"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-lg font-semibold text-gray-900 mb-1">Analytics</div>
                  <div className="text-sm text-gray-600">
                    View detailed system analytics
                  </div>
                </div>
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Brain className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </button>

            <button
              onClick={() => navigate("/admin/insights")}
              className="p-6 rounded-xl bg-rose-50 border border-rose-100 hover:bg-rose-100 transition-all text-left shadow-sm hover:shadow-md"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-lg font-semibold text-gray-900 mb-1">Insights</div>
                  <div className="text-sm text-gray-600">
                    Search respondents and view top coaches
                  </div>
                </div>
                <div className="p-2 bg-rose-100 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-rose-600" />
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Recent Activity
          </h2>
          <div className="text-gray-500 text-center py-12">
            Activity feed coming soon...
          </div>
        </div>
      </div>
    </div>
  );
}